import React, { useEffect, useMemo, useRef, useState } from "react"
import { ArrowLeftIcon, MagnifyingGlassIcon, XMarkIcon, EyeIcon, EyeSlashIcon, EnvelopeIcon, ArrowDownTrayIcon, DocumentTextIcon } from "@heroicons/react/24/outline"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import Tooltip from "aldoo-ra/Tooltip"
import { showSuccessToast, showErrorToast } from "aldoo-ra/Toast"
import { navigate } from "vike/client/router"
import useT from "../hooks/useT"
import useAsync from "../hooks/useAsync"
import useSearchParams from "../hooks/useSearchParams"
import useSiteLanguage from "../hooks/useSiteLanguage"
import Img from "../ui/Img"
import useOrders, { isLicenseKeyItem } from "./useOrders"
import { formatOrderDate } from "./Orders"
import { PlkOrderKeys, PlkPdfLink, plkPdfUrl, PlkSendEmail } from "../storeApi"
import { renderInstructions } from "../utils/productUtils"
import { buildContentUrl } from "../utils/contentRoutes"
import { Section, Card, Loading, ErrorText, Input, Select } from "./ui"

const copyText = async (text) => {
  try {
    if (navigator.clipboard?.writeText) return await navigator.clipboard.writeText(text)
    const el = document.createElement("textarea")
    el.value = text
    el.style.cssText = "position:fixed;top:-9999px;left:-9999px;opacity:0;"
    document.body.appendChild(el)
    el.select()
    document.execCommand("copy")
    el.remove()
  } catch {}
}

/** Old send-to-email dialog: "Send License Key" */
function SendKeyDialog({ keyId, onClose }) {
  const t = useT()
  const inputRef = useRef(null)
  const [email, setEmail] = useState("")
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState(null)

  useEffect(() => {
    const id = setTimeout(() => inputRef.current?.focus(), 100)
    const onKey = (e) => e.key === "Escape" && onClose()
    document.addEventListener("keydown", onKey)
    return () => {
      clearTimeout(id)
      document.removeEventListener("keydown", onKey)
    }
  }, [onClose])

  const send = async () => {
    if (!email.trim()) return
    setLoading(true)
    setResult(null)
    try {
      const response = await PlkSendEmail(keyId, email.trim())
      setResult(response?.success ? "success" : "error")
    } catch {
      setResult("error")
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[550] flex items-end sm:items-center justify-center bg-black/60 sm:p-4" onClick={onClose} data-testid="send-key-dialog">
      <div role="dialog" aria-modal="true" className="w-full max-w-[440px] rounded-t-[16px] sm:rounded-[16px] bg-paper p-5 flex flex-col gap-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-2 text-[20px] font-bold">
            <EnvelopeIcon className="w-6 h-6 text-primary" />
            {t`Send License Key`}
          </span>
          <Tooltip text={t`Close`}>
            <button type="button" onClick={onClose} aria-label={t`Close`} className="p-1 text-text_secondary hover:text-text">
              <XMarkIcon className="w-6 h-6" />
            </button>
          </Tooltip>
        </div>
        <p className="m-0 text-[14px] leading-relaxed text-text_secondary">{t`Enter an email address to send this license key to. The recipient will receive instructions on how to use it.`}</p>
        <Input ref={inputRef} type="email" placeholder={t`recipient@example.com`} value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} disabled={loading || result === "success"} />
        {result === "success" && <p className="m-0 rounded-lg bg-success px-4 py-3 text-[14px] font-semibold text-white">{t`Key sent successfully!`}</p>}
        {result === "error" && <ErrorText>{t`Failed to send. Please try again.`}</ErrorText>}
        {result !== "success" && (
          <div className="flex gap-2">
            <Button variant="secondary" className="flex-1 min-w-0" onClick={onClose}>
              {t`Cancel`}
            </Button>
            <Button variant="primary" className="flex-1 min-w-0" disabled={!email.trim() || loading} onClick={send}>
              {loading ? t`Sending...` : t`Send`}
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}

const PlatformIcon = ({ platform, storeLink }) => {
  if (!platform?.image) return null
  const img = <img src={platform.image} alt={platform.name} className="h-5 w-auto object-contain" />
  return (
    <Tooltip text={platform.name}>
      {storeLink ? (
        <a href={storeLink} target="_blank" rel="noopener noreferrer">
          {img}
        </a>
      ) : (
        img
      )}
    </Tooltip>
  )
}

/**
 * /user/software-licenses/product-license?orderId=… — the keys of one order
 * (product {plkOrderKeys}): search, platform filter, masked keys with reveal and
 * click-to-copy, per-key platform icons, send-by-email, CSV / PDF, "How to use".
 */
function ProductLicense({ orderId }) {
  const t = useT()
  const language = useSiteLanguage()
  const { result, pending } = useAsync(() => PlkOrderKeys(orderId), [orderId], { enabled: !!orderId })
  const [search, setSearch] = useState("")
  const [searchOpen, setSearchOpen] = useState(false)
  const [platformFilter, setPlatformFilter] = useState("")
  const [revealed, setRevealed] = useState({})
  const [copiedId, setCopiedId] = useState(null)
  const [sendKeyId, setSendKeyId] = useState(null)
  const [instructionsOpen, setInstructionsOpen] = useState(false)
  const [instructionsTab, setInstructionsTab] = useState(0)
  const searchRef = useRef(null)

  const keys = result?.keys || []
  const platforms = useMemo(() => {
    const map = {}
    for (const k of keys) if (k.platform?._id) map[k.platform._id] = k.platform
    return Object.values(map)
  }, [keys])
  const filtered = keys.filter((k) => (!search || k.key.toLowerCase().includes(search.toLowerCase())) && (!platformFilter || k.platform?._id === platformFilter))
  const platformEntries = (keys[0]?.product?.bookSettings?.platforms || []).filter((e) => e.platform && renderInstructions(e, language))

  if (!orderId) return <ErrorText>{t`No order specified.`}</ErrorText>
  if (pending && !result) return <Loading />
  if (result?.error || !keys.length) return <ErrorText>{result?.error ? t`Failed to load license keys.` : t`No license keys found for this order.`}</ErrorText>

  const productName = keys[0]?.product?.name || t`Software License`

  const copyKey = async (k) => {
    await copyText(k.key)
    setCopiedId(k._id)
    showSuccessToast(t`Key copied to clipboard`)
    setTimeout(() => setCopiedId(null), 2000)
  }

  const downloadCsv = () => {
    const rows = [["Key", "Product", "Platform"]]
    for (const k of keys) {
      const platformLabel = k.platform?.name || (k.product?.bookSettings?.platforms || []).map((e) => e.platform?.name).filter(Boolean).join(", ") || ""
      rows.push([k.key, k.product?.name || "", platformLabel])
    }
    const csv = rows.map((r) => r.map((v) => `"${v}"`).join(",")).join("\n")
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }))
    const a = document.createElement("a")
    a.href = url
    a.download = `license-keys-${result.order?.orderId || orderId}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const downloadPdf = async () => {
    const link = await PlkPdfLink(orderId).catch(() => null)
    if (!link?.token) return showErrorToast(t`Could not create the download link`)
    const a = document.createElement("a")
    a.href = plkPdfUrl({ orderId, ...link })
    a.download = `license-keys-${result.order?.orderId || orderId}.pdf`
    document.body.appendChild(a)
    a.click()
    a.remove()
  }

  return (
    <div className="flex flex-col gap-5" data-testid="product-license">
      <div className="flex items-center gap-2">
        <Tooltip text={t`Back`}>
          <button type="button" onClick={() => navigate("/user/software-licenses")} aria-label={t`Back`} className="p-1 rounded-full hover:bg-neutral">
            <ArrowLeftIcon className="w-6 h-6" />
          </button>
        </Tooltip>
        <Typography
          component="h2"
          variant="h4"
        >
          {productName}
        </Typography>
      </div>

      <div className="relative flex flex-row flex-wrap items-center justify-end gap-2">
        {searchOpen ? (
          <div className="absolute sm:static inset-0 z-10 flex items-center gap-1 bg-paper">
            <Input
              ref={searchRef}
              autoFocus
              placeholder={t`Search keys...`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-[36px] sm:w-[220px] pr-9"
              data-testid="license-search"
            />
            <span className="-ml-9 flex">
              <Tooltip text={t`Clear`}>
                <button
                  type="button"
                  aria-label={t`Clear`}
                  className="p-1 text-text_secondary"
                  onClick={() => (search ? setSearch("") : setSearchOpen(false))}
                >
                  <XMarkIcon className="w-4 h-4" />
                </button>
              </Tooltip>
            </span>
          </div>
        ) : (
          <Tooltip text={t`Search`}>
            <button type="button" aria-label={t`Search`} className="p-2 rounded-full hover:bg-neutral" onClick={() => setSearchOpen(true)}>
              <MagnifyingGlassIcon className="w-5 h-5" />
            </button>
          </Tooltip>
        )}
        {platforms.length > 0 && (
          <Select value={platformFilter} onChange={(e) => setPlatformFilter(e.target.value)} className="h-[36px] w-auto pr-8 text-[14px]" data-testid="license-platform-filter">
            <option value="">{t`All Platforms`}</option>
            {platforms.map((p) => (
              <option key={p._id} value={p._id}>
                {p.name}
              </option>
            ))}
          </Select>
        )}
      </div>

      <div className="rounded-lg border border-bordered overflow-hidden divide-y divide-bordered" data-testid="license-keys">
        {filtered.length === 0 && 
          <Typography variant="txt1_dim" className="block m-4">
            {t`No keys found.`}
          </Typography>
        }
        
        {filtered.map((k) => {
          const shown = !!revealed[k._id]
          return (
            <div key={k._id} className="flex items-center gap-3 px-3 sm:px-4 py-3">
              <Tooltip text={shown ? t`Hide key` : t`Show key`}>
                <button
                  type="button"
                  aria-label={shown ? t`Hide key` : t`Show key`}
                  className="p-1 rounded-full hover:bg-neutral"
                  onClick={() => setRevealed((r) => ({ ...r, [k._id]: !r[k._id] }))}
                >
                  {shown
                    ? <EyeSlashIcon className="w-5 h-5" />
                    : <EyeIcon className="w-5 h-5" />
                  }
                </button>
              </Tooltip>
              <button
                type="button"
                title={copiedId === k._id ? t`Copied!` : t`Copy key`}
                onClick={() => shown && copyKey(k)}
                className={`flex-1 min-w-0 truncate text-left font-mono text-[14px] font-semibold select-none ${shown ? "cursor-pointer text-primary tracking-wide" : "cursor-default text-text_secondary tracking-widest"}`}
                data-testid="license-key"
              >
                {shown ? (copiedId === k._id ? t`Copied!` : k.key) : "•".repeat(k.key.length)}
              </button>
              {k.platform ? (
                <PlatformIcon platform={k.platform} storeLink={k.platform.storeLink} />
              ) : (
                <span className="flex shrink-0 gap-3">
                  {(k.product?.bookSettings?.platforms || []).filter((e) => e.platform?.image).map((e) => (
                    <PlatformIcon key={e.platform._id} platform={e.platform} storeLink={e.storeLink} />
                  ))}
                </span>
              )}
              <Tooltip text={t`Send to email`}>
                <button type="button" aria-label={t`Send to email`} className="p-1 rounded-full hover:bg-neutral" onClick={() => setSendKeyId(k._id)}>
                  <EnvelopeIcon className="w-5 h-5" />
                </button>
              </Tooltip>
            </div>
          )
        })}
      </div>

      <div className="flex flex-row flex-wrap items-center justify-between gap-3">
        {platformEntries.length > 0 ? (
          <Button 
            variant={instructionsOpen ? "primary_s" : "secondary_s"} 
            onClick={() => setInstructionsOpen((o) => !o)}
          >
            {t`How to use`}
          </Button>
        ) : (
          <span />
        )}
        <div className="flex gap-2">
          <Button 
            variant="primary_dark_s" 
            onClick={downloadCsv}
          >
            <ArrowDownTrayIcon className="w-4 h-4" /> {t`CSV`}
          </Button>

          <Button 
            variant="primary_dark_s" 
            onClick={downloadPdf} 
            data-testid="license-pdf"
          >
            <DocumentTextIcon className="w-4 h-4" /> {t`PDF`}
          </Button>
        </div>
      </div>

      {instructionsOpen && platformEntries.length > 0 && (
        <div className="rounded-lg border border-bordered overflow-hidden">
          <div className="flex flex-row flex-wrap border-b border-bordered">
            {platformEntries.map((entry, i) => (
              <button key={i} type="button" onClick={() => setInstructionsTab(i)} className={`flex items-center gap-1.5 px-4 py-2 text-[14px] font-semibold border-b-2 ${instructionsTab === i ? "border-primary text-primary" : "border-transparent text-text_secondary"}`}>
                {entry.platform.image && <img src={entry.platform.image} alt="" className="h-4 w-auto object-contain opacity-60" />}
                {entry.platform.name}
              </button>
            ))}
          </div>
          {platformEntries[instructionsTab] && <div className="sf-html p-4 text-[14px] leading-[1.8]" dangerouslySetInnerHTML={{ __html: renderInstructions(platformEntries[instructionsTab], language) }} />}
        </div>
      )}

      {sendKeyId && <SendKeyDialog keyId={sendKeyId} onClose={() => setSendKeyId(null)} />}
    </div>
  )
}

/** Every software-license product bought (old User/softwareLicenses.jsx). */
export default function SoftwareLicenses({ sub }) {
  const t = useT()
  const language = useSiteLanguage()
  const params = useSearchParams()
  const { orders, pending, error } = useOrders()

  if (sub === "product-license") {
    return (
      <Section title={t`Software Licenses`}>
        <ProductLicense orderId={params.get("orderId")} />
      </Section>
    )
  }

  const entries = orders.flatMap((order) => order.items.filter(isLicenseKeyItem).map((item) => ({ item, orderId: order._id, orderNumber: order.orderId, date: order.date })))

  return (
    <Section title={t`Software Licenses`}>
      {pending && <Loading />}
      <ErrorText>{error?.message}</ErrorText>
      {!pending && !error && entries.length === 0 && (
        <div className="py-16 text-center">
          <p className="m-0 text-[20px] text-text_secondary">{t`No software licenses yet`}</p>
          <p className="m-0 mt-4 text-[14px] text-text_secondary">{t`Your purchased software licenses will appear here.`}</p>
        </div>
      )}
      <div 
        className="flex flex-col gap-3" 
        data-testid="software-licenses"
      >
        {entries.map(({ item, orderId, orderNumber, date }, i) => (
          <Card 
            key={`${orderId}-${i}`} 
            className="flex flex-col gap-3"
          >
            <div className="flex items-center gap-3">
              <Img 
                src={item.square || item.thumb} 
                alt={item.name} 
                width={44} 
                height={44} 
                className="w-11 h-11 rounded-lg overflow-hidden shrink-0" 
              />

              <div className="flex flex-col min-w-0">
                <Typography component="span" variant="body1_accent">
                  {item.name}
                </Typography>

                {item.author_display_name && 
                  <Typography variant="txt2_dim">
                    {item.author_display_name}
                  </Typography>
                }
              </div>
            </div>

            <div className="flex flex-row flex-wrap items-center justify-between gap-2">
              <Typography variant="txt1_dim">
                {orderNumber ? `${t`Order`} #${orderNumber}` : ""}
                {date ? ` · ${formatOrderDate(date, language)}` : ""}
              </Typography>

              <div className="flex flex-row flex-wrap items-center gap-2">
                {/* a licence is bought again for another seat or device: its product page sells more */}
                <Button variant="secondary_s" href={buildContentUrl(item)} className="min-w-0" data-testid="buy-more-licenses">
                  {t`Buy more`}
                </Button>
                <Button 
                  variant="primary_dark_s" 
                  href={`/user/software-licenses/product-license?orderId=${orderId}`} 
                  className="min-w-0" 
                  data-testid="view-licenses"
                >
                  {item.quantity ? `${item.quantity} ${item.quantity === 1 ? t`license` : t`licenses`}` : t`View Licenses`}
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </Section>
  )
}
